import type { RouteRecordRaw } from 'vue-router'

/**
 * Alert Engine routes — mounted as children of /MOPLayout in unified-routes.ts.
 * Folder layout mirrors queueManagement's pattern:
 *   layouts/       → module shell
 *   pages/<Dashboard|Masters/<Entity>>/  → page components
 *   services/      → module-level services (menu, common, accessControl)
 *
 * Wiring a new master = 3 steps:
 *   1. Add the route to this file (list page; create/edit uses a MOPDialog inside).
 *   2. Add the SubMenuName → path entry to ROUTE_MAP in
 *      `pages/Dashboard/AlertEngineDashboard.vue` so the dashboard tile click navigates.
 *   3. (Optional) add an `imagePathFor` keyword in the dashboard so the tile shows
 *      the proper PNG/SVG from `assets/Img/alertengine_imgs/`.
 *
 * Migrated so far: Vendor, Header, Channel Sub Type, Credit Conversion (Credit
 * Plan Setup), Credit Plan (Credit Price Setup).
 */
export const alertEngineRoutes: RouteRecordRaw[] = [
  {
    path: 'alert-engine',
    component: () => import('@/modules/alertEngine/layouts/AlertEngineLayout.vue'),
    meta: { title: 'Alert Engine', requiresAuth: true },
    children: [
      {
        path: '',
        redirect: { name: 'AlertEngineDashboard' }
      },
      {
        path: 'dashboard',
        name: 'AlertEngineDashboard',
        component: () => import('@/modules/alertEngine/pages/Dashboard/AlertEngineDashboard.vue'),
        meta: { title: 'Dashboard', requiresAuth: true, breadcrumb: 'Dashboard' }
      },

      // ── Vendor master ──────────────────────────────────────
      {
        path: 'vendor',
        name: 'VendorList',
        component: () => import('@/modules/alertEngine/pages/Masters/Vendor/VendorList.vue'),
        meta: { title: 'Vendor Master', requiresAuth: true, breadcrumb: 'Vendor' }
      },

      // ── Header master ──────────────────────────────────────
      {
        path: 'header',
        name: 'HeaderList',
        component: () => import('@/modules/alertEngine/pages/Masters/Header/HeaderList.vue'),
        meta: { title: 'Header Master', requiresAuth: true, breadcrumb: 'Header' }
      },

      // ── Channel Sub Type master ────────────────────────────
      {
        path: 'channel-sub-type',
        name: 'ChannelSubTypeList',
        component: () => import('@/modules/alertEngine/pages/Masters/ChannelSubType/ChannelSubTypeList.vue'),
        meta: { title: 'Channel Sub Type Master', requiresAuth: true, breadcrumb: 'Channel Sub Type' }
      },

      // ── Credit Conversion (Credit Plan) master ─────────────
      {
        path: 'credit-conversion',
        name: 'CreditConversionList',
        component: () => import('@/modules/alertEngine/pages/Masters/CreditConversion/CreditConversionList.vue'),
        meta: { title: 'Credit Plan Master', requiresAuth: true, breadcrumb: 'Credit Conversion' }
      },

      // ── Credit Plan (Credit Price Setup) master ────────────
      {
        path: 'credit-plan',
        name: 'CreditPlanList',
        component: () => import('@/modules/alertEngine/pages/Masters/CreditPlan/CreditPlanList.vue'),
        meta: { title: 'Credit Price Master', requiresAuth: true, breadcrumb: 'Credit Price' }
      },

      // ── Message Class master ───────────────────────────────
      // Mirrors MoceroUI's separate-page flow: list, then Create / Edit
      // open on their own routes (the legacy MOP UI used a dialog).
      {
        path: 'message-class',
        name: 'MessageClassList',
        component: () => import('@/modules/alertEngine/pages/Masters/MessageClass/MessageClassList.vue'),
        meta: { title: 'Message Class Master', requiresAuth: true, breadcrumb: 'Message Class' }
      },
      {
        path: 'message-class/create',
        name: 'MessageClassCreate',
        component: () => import('@/modules/alertEngine/pages/Masters/MessageClass/MessageClassForm.vue'),
        meta: { title: 'Create Message Class', requiresAuth: true, breadcrumb: 'Create' }
      },
      {
        path: 'message-class/edit/:id',
        name: 'MessageClassEdit',
        component: () => import('@/modules/alertEngine/pages/Masters/MessageClass/MessageClassForm.vue'),
        props: true,
        meta: { title: 'Edit Message Class', requiresAuth: true, breadcrumb: 'Edit' }
      },
      {
        path: 'message-class/view/:id',
        name: 'MessageClassDetails',
        component: () => import('@/modules/alertEngine/pages/Masters/MessageClass/MessageClassDetailsView.vue'),
        props: true,
        meta: { title: 'Message Class Details', requiresAuth: true, breadcrumb: 'Details' }
      },

      // ── Package master ─────────────────────────────────────
      // Mirrors MoceroUI's separate-page flow: list, then Create / Edit
      // open on their own routes (the legacy MOP UI used a dialog).
      {
        path: 'package',
        name: 'PackageList',
        component: () => import('@/modules/alertEngine/pages/Masters/Package/PackageList.vue'),
        meta: { title: 'Package Master', requiresAuth: true, breadcrumb: 'Package' }
      },
      {
        path: 'package/create',
        name: 'PackageCreate',
        component: () => import('@/modules/alertEngine/pages/Masters/Package/PackageForm.vue'),
        meta: { title: 'Create Package', requiresAuth: true, breadcrumb: 'Create' }
      },
      {
        path: 'package/edit/:id',
        name: 'PackageEdit',
        component: () => import('@/modules/alertEngine/pages/Masters/Package/PackageForm.vue'),
        props: true,
        meta: { title: 'Edit Package', requiresAuth: true, breadcrumb: 'Edit' }
      },
      {
        path: 'package/view/:id',
        name: 'PackageDetails',
        component: () => import('@/modules/alertEngine/pages/Masters/Package/PackageDetailsView.vue'),
        props: true,
        meta: { title: 'Package Details', requiresAuth: true, breadcrumb: 'Details' }
      },

      // ── SMS Template master (Templates/Sms) ────────────────
      // First of 5 template channels (SMS, WhatsApp, IVR, Notification, Email).
      // All share the `TemplateMaster/*` backend, scoped by `Type` (SMS=1).
      // Create/Edit are routed pages (not dialogs) to match MoceroUI's
      // `/smsTemplateCreation?Id=...` flow — the form is long enough that a
      // dialog would feel cramped.
      {
        path: 'sms-template',
        name: 'SmsTemplateList',
        component: () => import('@/modules/alertEngine/pages/Templates/Sms/SmsTemplateList.vue'),
        meta: { title: 'SMS Template Master', requiresAuth: true, breadcrumb: 'SMS Template' }
      },
      {
        path: 'sms-template/create',
        name: 'SmsTemplateCreate',
        component: () => import('@/modules/alertEngine/pages/Templates/Sms/SmsTemplateForm.vue'),
        meta: { title: 'Create SMS Template', requiresAuth: true, breadcrumb: 'Create' }
      },
      {
        path: 'sms-template/edit/:id',
        name: 'SmsTemplateEdit',
        component: () => import('@/modules/alertEngine/pages/Templates/Sms/SmsTemplateForm.vue'),
        props: true,
        meta: { title: 'Edit SMS Template', requiresAuth: true, breadcrumb: 'Edit' }
      },

      // ── WhatsApp Template master (Templates/WhatsApp) ──────
      // Second template channel. Same `TemplateMaster/*` backend, scoped
      // by `Type=2`. Adds Header (text), Footer, FileType fields on top
      // of the SMS contract. Buttons/CTA UI deferred.
      {
        path: 'whatsapp-template',
        name: 'WhatsAppTemplateList',
        component: () => import('@/modules/alertEngine/pages/Templates/WhatsApp/WhatsAppTemplateList.vue'),
        meta: { title: 'WhatsApp Template Master', requiresAuth: true, breadcrumb: 'WhatsApp Template' }
      },
      {
        path: 'whatsapp-template/create',
        name: 'WhatsAppTemplateCreate',
        component: () => import('@/modules/alertEngine/pages/Templates/WhatsApp/WhatsAppTemplateForm.vue'),
        meta: { title: 'Create WhatsApp Template', requiresAuth: true, breadcrumb: 'Create' }
      },
      {
        path: 'whatsapp-template/edit/:id',
        name: 'WhatsAppTemplateEdit',
        component: () => import('@/modules/alertEngine/pages/Templates/WhatsApp/WhatsAppTemplateForm.vue'),
        props: true,
        meta: { title: 'Edit WhatsApp Template', requiresAuth: true, breadcrumb: 'Edit' }
      },

      // ── IVR Template master (Templates/Ivr) ────────────────
      // Channel 3 in the TemplateMaster contract — voice-script body
      // alongside the same Module/Entity cascade. Filters by ChannelTypeId == 3.
      {
        path: 'ivr-template',
        name: 'IvrTemplateList',
        component: () => import('@/modules/alertEngine/pages/Templates/Ivr/IvrTemplateList.vue'),
        meta: { title: 'IVR Template Master', requiresAuth: true, breadcrumb: 'IVR Template' }
      },
      {
        path: 'ivr-template/create',
        name: 'IvrTemplateCreate',
        component: () => import('@/modules/alertEngine/pages/Templates/Ivr/IvrTemplateForm.vue'),
        meta: { title: 'Create IVR Template', requiresAuth: true, breadcrumb: 'Create' }
      },
      {
        path: 'ivr-template/edit/:id',
        name: 'IvrTemplateEdit',
        component: () => import('@/modules/alertEngine/pages/Templates/Ivr/IvrTemplateForm.vue'),
        props: true,
        meta: { title: 'Edit IVR Template', requiresAuth: true, breadcrumb: 'Edit' }
      },

      // ── Notification Template master (Templates/Notification) ──
      // Channel 4 in the TemplateMaster contract — push/in-app notifications
      // with Title + Body + Header/Footer + Category. Filters Channel Sub
      // Type by ChannelTypeId == 4; vendor list is unfiltered (matches IVR
      // fix where the filtered list ends up empty).
      {
        path: 'notification-template',
        name: 'NotificationTemplateList',
        component: () => import('@/modules/alertEngine/pages/Templates/Notification/NotificationTemplateList.vue'),
        meta: { title: 'Notification Template Master', requiresAuth: true, breadcrumb: 'Notification Template' }
      },
      {
        path: 'notification-template/create',
        name: 'NotificationTemplateCreate',
        component: () => import('@/modules/alertEngine/pages/Templates/Notification/NotificationTemplateForm.vue'),
        meta: { title: 'Create Notification Template', requiresAuth: true, breadcrumb: 'Create' }
      },
      {
        path: 'notification-template/edit/:id',
        name: 'NotificationTemplateEdit',
        component: () => import('@/modules/alertEngine/pages/Templates/Notification/NotificationTemplateForm.vue'),
        props: true,
        meta: { title: 'Edit Notification Template', requiresAuth: true, breadcrumb: 'Edit' }
      },

      // ── Email Template master (Templates/Email) ────────────
      // Channel 5 — completes the template family. Same `TemplateMaster/*`
      // backbone, scoped by `Type=5`. Title repurposed as email Subject,
      // Message as the body. Filters dropped per the channel-template
      // pattern users approved on IVR / Notification.
      {
        path: 'email-template',
        name: 'EmailTemplateList',
        component: () => import('@/modules/alertEngine/pages/Templates/Email/EmailTemplateList.vue'),
        meta: { title: 'Email Template Master', requiresAuth: true, breadcrumb: 'Email Template' }
      },
      {
        path: 'email-template/create',
        name: 'EmailTemplateCreate',
        component: () => import('@/modules/alertEngine/pages/Templates/Email/EmailTemplateForm.vue'),
        meta: { title: 'Create Email Template', requiresAuth: true, breadcrumb: 'Create' }
      },
      {
        path: 'email-template/edit/:id',
        name: 'EmailTemplateEdit',
        component: () => import('@/modules/alertEngine/pages/Templates/Email/EmailTemplateForm.vue'),
        props: true,
        meta: { title: 'Edit Email Template', requiresAuth: true, breadcrumb: 'Edit' }
      },

      // ── Client master (Masters/Client) ─────────────────────
      // The big one — MoceroUI's `clientCreation.vue` is a 4-tab stepper:
      // Basic Details → Channel Setup → Credit Purchase Plan → MC + Packages.
      // This migration ships the Basic Details tab fully wired plus stub
      // panels for the remaining three (each backed by its own service —
      // ClientChannelSetup / ClientCreditPlanMapping / ClientMC mappings).
      {
        path: 'client',
        name: 'ClientList',
        component: () => import('@/modules/alertEngine/pages/Masters/Client/ClientList.vue'),
        meta: { title: 'Client Master', requiresAuth: true, breadcrumb: 'Client' }
      },
      {
        path: 'client/create',
        name: 'ClientCreate',
        component: () => import('@/modules/alertEngine/pages/Masters/Client/ClientForm.vue'),
        meta: { title: 'Create Client', requiresAuth: true, breadcrumb: 'Create' }
      },
      {
        path: 'client/edit/:id',
        name: 'ClientEdit',
        component: () => import('@/modules/alertEngine/pages/Masters/Client/ClientForm.vue'),
        props: true,
        meta: { title: 'Edit Client', requiresAuth: true, breadcrumb: 'Edit' }
      }
    ]
  }
]
